import { HIT_AREA_24 } from '../ui/hitArea';
import { useEffect, useState, type FormEvent } from 'react';
import { ArrowLeft, KeyRound, Lock, ShieldCheck, UserRound, X } from 'lucide-react';
import { ROLE_NAME, errorMessage, useLocale } from '../../i18n';
import { ConfirmButton } from '../ui/ConfirmButton';
import { OwnRolesSection } from './OwnRolesSection';
import {
  fetchOrganizationAccess,
  grantOrganizationAccess,
  revokeOrganizationAccess,
  type AccessListResult,
  type AccessPerson,
  type AccessRoleRef,
  type AccessWriteResult,
} from '../../lib/api/organizationAccess';

// ─────────────────────────────────────────────────────────────────────
// TILGANGER — the organization's own access page (slice 3, 2026-10-18).
//
// Until now every role grant happened in /sa, by Starpoint. This page lets a
// centre or a club hand out roles to its own people, under the rules the owner
// decided on 2026-10-18 and the SERVER enforces — the page draws them so that
// nobody discovers a refusal on use, but it decides nothing:
//
//   1A  only somebody already linked to this organization — a role holder, or
//       an active roster member with an account — can be given a role. There
//       is no invitation here; the lead says where new people come from.
//   2B  nobody hands out, or takes away, a right they do not hold themselves.
//       The server says per role what lies beyond the caller (`beyondYou`),
//       and the page greys those out and says why. A revoke of such a role is
//       not offered either: the same rule, the other direction.
//   3A  the last person who may hand out roles cannot be removed. The server
//       decides that under a lock; the page shows the refusal as a sentence.
//
// Every string is a catalog key. Role NAMES follow «Min konto»: the catalog's
// name for a system role, the organization's own name for a custom one.
// ─────────────────────────────────────────────────────────────────────

export interface OrganizationAccessViewProps {
  /** The organization the portal is acting in — for the heading only. */
  organizationName: string | null;
  /** The signed-in account, so its own row says so. */
  currentUserId: string | null;
  /** Back to the settings this page was opened from. */
  onBack: () => void;
  /** Injectable for tests. */
  load?: typeof fetchOrganizationAccess;
  grant?: typeof grantOrganizationAccess;
  revoke?: typeof revokeOrganizationAccess;
}

type Notice = { kind: 'ok' | 'error'; text: string } | null;

export function OrganizationAccessView({
  organizationName,
  currentUserId,
  onBack,
  load = fetchOrganizationAccess,
  grant = grantOrganizationAccess,
  revoke = revokeOrganizationAccess,
}: OrganizationAccessViewProps) {
  const { t } = useLocale();
  const [result, setResult] = useState<AccessListResult | null>(null);
  const [generation, setGeneration] = useState(0);
  const [personId, setPersonId] = useState('');
  const [roleId, setRoleId] = useState('');
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<Notice>(null);

  // ONE FRESH LIST PER OPEN, AND AFTER EVERY WRITE. The server's answer is the
  // only truth about who holds what; the page never edits its copy to match
  // what it just asked for.
  useEffect(() => {
    let live = true;
    void load().then((r) => {
      if (live) setResult(r);
    });
    return () => {
      live = false;
    };
  }, [load, generation]);

  const reload = () => setGeneration((n) => n + 1);

  const roleName = (role: AccessRoleRef): string => {
    const key = ROLE_NAME[role.key];
    return key ? t(key) : role.name;
  };
  // An account with no display name is named by the catalog and a short id
  // (#1144), not by its raw uuid. Not by its e-mail: the access list does not
  // send addresses, and showing them to every role administrator is a
  // disclosure this screen has not needed until now.
  const personName = (p: AccessPerson): string =>
    p.displayName || t('access.people.unnamed', { id: p.userId.slice(0, 8) });

  const list = result?.state === 'ok' ? result.list : null;
  const roleById = new Map((list?.roles ?? []).map((r) => [r.id, r] as const));
  /** The capabilities a role carries past the caller — empty means «yours to give». */
  const beyondYou = (id: string): string[] => roleById.get(id)?.beyondYou ?? [];

  const settle = (outcome: AccessWriteResult, done: string, unchanged: string) => {
    if (outcome.state === 'ok') {
      setNotice({ kind: 'ok', text: outcome.changed ? done : unchanged });
      reload();
      return;
    }
    if (outcome.state === 'forbidden') {
      setNotice({ kind: 'error', text: t('access.forbidden') });
      return;
    }
    if (outcome.state === 'refused') {
      // THE CODE BECOMES A SENTENCE HERE and nowhere earlier: the server
      // answered `last_access_manager`, and which language that is said in is
      // the browser's business.
      setNotice({ kind: 'error', text: errorMessage(t, outcome.reason) });
      return;
    }
    setNotice({ kind: 'error', text: t('errors.unknown') });
  };

  const doGrant = async () => {
    const person = list?.people.find((p) => p.userId === personId);
    const role = roleById.get(roleId);
    if (!person || !role || busy) return;
    setBusy(true);
    setNotice(null);
    const name = personName(person);
    const roleLabel = roleName(role);
    settle(
      await grant(person.userId, role.id),
      t('access.grant.done', { name, role: roleLabel }),
      t('access.grant.alreadyHeld', { name, role: roleLabel }),
    );
    setRoleId('');
    setBusy(false);
  };

  const doRevoke = async (person: AccessPerson, role: AccessRoleRef) => {
    if (busy) return;
    setBusy(true);
    setNotice(null);
    const name = personName(person);
    const roleLabel = roleName(role);
    // «No longer holds» is true whether the server removed a row or found
    // none — the end state is what the person asked for.
    const done = t('access.revoke.done', { name, role: roleLabel });
    settle(await revoke(person.userId, role.id), done, done);
    setBusy(false);
  };

  const submitGrant = (e: FormEvent) => {
    e.preventDefault();
    void doGrant();
  };

  const body = () => {
    if (result === null) {
      return (
        <p className="text-sm text-ink-3" data-testid="access-loading">
          {t('access.loading')}
        </p>
      );
    }
    if (result.state === 'forbidden') {
      return (
        <p className="text-sm text-danger" data-testid="access-forbidden">
          {t('access.forbidden')}
        </p>
      );
    }
    if (result.state !== 'ok') {
      return (
        <div data-testid="access-unavailable">
          <p className="text-sm text-danger">{t('access.unavailable')}</p>
          <button type="button" onClick={reload} className="btn-outline text-xs mt-2">
            {t('access.retry')}
          </button>
        </div>
      );
    }
    const { people, roles } = result.list;
    return (
      <div className="space-y-4">
        {notice && (
          <p
            role="status"
            data-testid="access-notice"
            className={`text-sm ${notice.kind === 'ok' ? 'text-ink-2' : 'text-danger'}`}
          >
            {notice.text}
          </p>
        )}

        {/* WHO HOLDS WHAT. Each chip is a role; the × on it is the revoke, and
            it is absent — not merely disabled — for a role beyond the caller:
            a control that can only ever refuse is a worse answer than the
            lock that says why. */}
        <section className="card p-5" data-testid="access-people">
          <h2 className="font-display text-lg font-bold text-ink inline-flex items-center gap-2">
            <UserRound size={18} /> {t('access.people.title')}
          </h2>
          <p className="mt-1 text-xs text-ink-3">{t('access.people.lead')}</p>
          {people.length === 0 ? (
            <p className="mt-3 text-sm text-ink-3" data-testid="access-people-none">
              {t('access.people.none')}
            </p>
          ) : (
            <ul className="mt-3 rounded-lg border border-line overflow-hidden">
              {people.map((p, i) => (
                <li
                  key={p.userId}
                  data-testid={`access-person-${p.userId}`}
                  className={`flex flex-col sm:flex-row sm:items-center justify-between gap-2 px-3 py-2 ${
                    i > 0 ? 'border-t border-line-soft' : ''
                  }`}
                >
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-ink truncate">
                      {personName(p)}
                      {p.userId === currentUserId && (
                        <span className="ml-1 font-normal text-ink-3">
                          ({t('access.people.you')})
                        </span>
                      )}
                    </div>
                    <div className="text-[11px] text-ink-3 flex flex-wrap gap-x-2">
                      {p.status !== 'active' && (
                        <span className="text-danger">{t('access.people.disabled')}</span>
                      )}
                      {p.onRoster && <span>{t('access.people.onRoster')}</span>}
                      {p.roles.length === 0 && <span>{t('access.people.noRoles')}</span>}
                    </div>
                  </div>
                  <div className="flex flex-wrap sm:justify-end gap-1.5">
                    {p.roles.map((r) => {
                      const missing = beyondYou(r.id);
                      return (
                        <span
                          key={r.id}
                          className="inline-flex items-center gap-1 rounded-full border border-line bg-bg-soft pl-2.5 pr-1 py-0.5 text-xs text-ink"
                          title={r.key}
                          data-testid={`access-chip-${p.userId}-${r.key}`}
                        >
                          {roleName(r)}
                          {missing.length > 0 ? (
                            <span
                              className="inline-flex p-0.5 text-ink-3"
                              title={`${t('access.beyondYou')}: ${t('access.beyondYou.help', {
                                capabilities: missing.join(', '),
                              })}`}
                              data-testid={`access-locked-${p.userId}-${r.key}`}
                            >
                              <Lock size={11} />
                            </span>
                          ) : (
                            <ConfirmButton
                              question={t('access.revoke.question', {
                                role: roleName(r),
                                name: personName(p),
                              })}
                              confirmLabel={t('access.revoke.confirm')}
                              onConfirm={() => void doRevoke(p, r)}
                              className={`${HIT_AREA_24} inline-flex rounded-full p-0.5 text-ink-3 hover:text-rose`}
                              title={t('access.revoke.confirm')}
                              disabled={busy}
                            >
                              <X size={12} />
                            </ConfirmButton>
                          )}
                        </span>
                      );
                    })}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>

        {/* GIVE A ROLE. The person list is the server's — everybody linked here
            (1A) — and a role past the caller is offered greyed out with the
            reason in its label (2B). */}
        <section className="card p-5" data-testid="access-grant">
          <h2 className="font-display text-lg font-bold text-ink inline-flex items-center gap-2">
            <KeyRound size={18} /> {t('access.grant.title')}
          </h2>
          <form
            onSubmit={submitGrant}
            className="mt-3 grid gap-3 sm:grid-cols-[1fr_1fr_auto] sm:items-end"
          >
            <label className="block">
              <span className="font-mono uppercase tracking-wider text-xs text-ink-3">
                {t('access.grant.person')}
              </span>
              <select
                className="input mt-1 w-full"
                value={personId}
                onChange={(e) => setPersonId(e.target.value)}
                data-testid="access-grant-person"
              >
                <option value="">{t('access.grant.choosePerson')}</option>
                {people.map((p) => (
                  <option key={p.userId} value={p.userId}>
                    {personName(p)}
                  </option>
                ))}
              </select>
            </label>
            <label className="block">
              <span className="font-mono uppercase tracking-wider text-xs text-ink-3">
                {t('access.grant.role')}
              </span>
              <select
                className="input mt-1 w-full"
                value={roleId}
                onChange={(e) => setRoleId(e.target.value)}
                data-testid="access-grant-role"
              >
                <option value="">{t('access.grant.chooseRole')}</option>
                {roles.map((r) => (
                  <option key={r.id} value={r.id} disabled={r.beyondYou.length > 0}>
                    {r.beyondYou.length > 0
                      ? `${roleName(r)} — ${t('access.beyondYou')}`
                      : roleName(r)}
                  </option>
                ))}
              </select>
            </label>
            <button
              type="submit"
              className="btn-lime text-xs"
              disabled={busy || !personId || !roleId}
              data-testid="access-grant-submit"
            >
              {busy ? t('access.grant.working') : t('access.grant.submit')}
            </button>
          </form>
        </section>

        {/* THE CEILING, SPELLED OUT. What this organization may hand out at
            all, and which of it lies beyond this caller — so the greyed-out
            option above has a sentence somewhere. */}
        <section className="card p-5" data-testid="access-roles">
          <h2 className="font-display text-lg font-bold text-ink inline-flex items-center gap-2">
            <ShieldCheck size={18} /> {t('access.roles.title')}
          </h2>
          <p className="mt-1 text-xs text-ink-3">{t('access.roles.lead')}</p>
          <ul className="mt-3 space-y-2">
            {roles.map((r) => (
              <li
                key={r.id}
                data-testid={`access-role-${r.key}`}
                className="rounded-lg border border-line px-3 py-2"
              >
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-sm font-medium text-ink" title={r.key}>
                    {roleName(r)}
                  </span>
                  {!r.isSystem && (
                    <span className="text-[11px] text-ink-3">{t('access.roles.custom')}</span>
                  )}
                  {r.beyondYou.length > 0 && (
                    <span
                      className="inline-flex items-center gap-1 rounded-full border border-line px-2 py-0.5 text-[11px] text-ink-3"
                      title={t('access.beyondYou.help', { capabilities: r.beyondYou.join(', ') })}
                    >
                      <Lock size={10} /> {t('access.beyondYou')}
                    </span>
                  )}
                </div>
                {r.description && <p className="mt-0.5 text-xs text-ink-3">{r.description}</p>}
              </li>
            ))}
          </ul>
        </section>

        {/* EGNE ROLLER (2026-11-01): the organization's own roles, defined and
            changed here under the server's ceiling. Only an access manager
            reaches this page at all — the list above is refused to anyone else. */}
        <OwnRolesSection
          roles={roles}
          offerable={result.list.ownRoles?.offerable ?? []}
          holdersOf={(id) => people.filter((p) => p.roles.some((r) => r.id === id)).length}
          onSettled={(next, again) => {
            setNotice(next);
            if (again) reload();
          }}
        />
      </div>
    );
  };

  return (
    // `max-w-4xl`, like the settings it is opened from — see the note there
    // (2026-10-21). The people list and the grant form are rows and a grid,
    // so they use the width; the role list stays one column and reads fine.
    <div className="max-w-4xl mx-auto px-4 md:px-6 py-10 slide-up" data-testid="access-view">
      <button
        type="button"
        onClick={onBack}
        className="text-xs font-mono text-ink-3 hover:text-ink-2 mb-2 flex items-center gap-1"
        data-testid="access-back"
      >
        <ArrowLeft size={12} /> {t('access.back')}
      </button>
      <div className="mb-6">
        <h1 className="font-display text-3xl font-semibold mb-1">{t('access.title')}</h1>
        <p className="text-sm text-ink-3">
          {t('access.lead', { organization: organizationName ?? t('access.thisOrganization') })}
        </p>
      </div>
      {body()}
    </div>
  );
}
